// Знак Оригинала Клаустрофобии (quests/_original_mark.html.erb, _quest.ts →
// createOriginalMarkHTML). Два дела:
// 1. Подсказка. На десктопе её показывает CSS по наведению и фокусу с клавиатуры;
//    на телефоне наведения нет, поэтому нажатие на ключ открывает подсказку,
//    нажатие мимо, повторное нажатие или Esc закрывает. Закрытие при живом
//    фокусе или наведении — класс is-dismissed: CSS не показывает подсказку,
//    пока указатель не уйдёт со знака или фокус не вернётся заново. Обработчики
//    одни на документ: карточки каталога дорисовываются скриптом.
// 2. Экономия на телефоне: у каждого знака три CSS-анимации, в каталоге их
//    десятки, поэтому у знаков вне экрана анимация стоит на паузе (is-offscreen).
const OPEN_CLASS = 'is-open'
const DISMISSED_CLASS = 'is-dismissed'
const OFFSCREEN_CLASS = 'is-offscreen'
const MARK_SELECTOR = '[data-original-mark]'
const TOGGLE_SELECTOR = '[data-original-mark-toggle]'

function markOf(target: EventTarget | null): HTMLElement | null {
  return (target as HTMLElement | null)?.closest?.(MARK_SELECTOR) ?? null
}

function dismiss(mark: HTMLElement): void {
  mark.classList.remove(OPEN_CLASS)
  mark.classList.add(DISMISSED_CLASS)
}

// Закрыть все открытые подсказки, кроме except; знак с фокусом тоже гасим,
// иначе его подсказку продолжит показывать focus-visible.
function closeAll(except?: HTMLElement | null): void {
  document
    .querySelectorAll<HTMLElement>(`${MARK_SELECTOR}.${OPEN_CLASS}`)
    .forEach((mark) => {
      if (mark !== except) dismiss(mark)
    })
  const focused = markOf(document.activeElement)
  if (focused && focused !== except) dismiss(focused)
}

function initTooltips(): void {
  document.addEventListener('click', (event: Event) => {
    const toggle = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      TOGGLE_SELECTOR
    )
    const mark = toggle ? markOf(toggle) : null

    closeAll(mark)
    if (!mark) return

    // Карточка целиком — ссылка на квест (оверлей заголовка); нажатие на ключ
    // должно показать подсказку, а не увести со страницы.
    event.preventDefault()
    if (mark.classList.contains(OPEN_CLASS)) {
      dismiss(mark)
    } else {
      mark.classList.remove(DISMISSED_CLASS)
      mark.classList.add(OPEN_CLASS)
    }
  })

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return

    // Фокус остаётся на кнопке, показ подавляем (closeAll гасит и знак с фокусом).
    closeAll()
  })

  // Указатель ушёл со знака или фокус покинул его: следующее наведение или
  // фокус снова показывают подсказку.
  const release = (event: Event) => {
    const mark = markOf(event.target)
    const next = (event as PointerEvent | FocusEvent)
      .relatedTarget as Node | null
    if (mark && !(next && mark.contains(next))) {
      mark.classList.remove(DISMISSED_CLASS)
    }
  }
  document.addEventListener('pointerout', release)
  document.addEventListener('focusout', release)
}

function initOffscreenPause(): void {
  if (!('IntersectionObserver' in window)) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle(OFFSCREEN_CLASS, !entry.isIntersecting)
      })
    },
    { rootMargin: '100px 0px' }
  )
  const marksIn = (node: Node): HTMLElement[] => {
    if (!(node instanceof HTMLElement)) return []
    const nested = Array.from(node.querySelectorAll<HTMLElement>(MARK_SELECTOR))
    return node.matches(MARK_SELECTOR) ? [node, ...nested] : nested
  }

  marksIn(document.body).forEach((mark) => observer.observe(mark))

  // Карточки каталога появляются и исчезают при фильтрации и «Показать ещё»:
  // новые — под наблюдение, удалённые — снять, иначе наблюдатель держит их в памяти.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        marksIn(node).forEach((mark) => {
          if (!mark.isConnected) observer.unobserve(mark)
        })
      })
      mutation.addedNodes.forEach((node) => {
        marksIn(node).forEach((mark) => observer.observe(mark))
      })
    })
  }).observe(document.body, { childList: true, subtree: true })
}

export function initOriginalMarks(): void {
  initTooltips()
  initOffscreenPause()
}
